// Système de Progression RPG, Points de Vie (PV), Expérience (XP) et Déblocages MonAlerte

export const checkUnlockPrerequisite = (tileId, unlockedFeatures = []) => {
  // 1. Montre connectée nécessite d'avoir débloqué la Touche Raccourcis
  if (tileId === 'smartwatch') {
    const hasShortcut = unlockedFeatures.includes('shortcutHardware');
    if (!hasShortcut) {
      return {
        met: false,
        reason: 'Nécessite d’avoir débloqué la Touche Raccourcis (🔘)',
        requiredTitle: '🔘 Touche Raccourcis (60 XP)',
      };
    }
  }

  // 3. Capture Vidéo nécessite d'avoir débloqué la Capture Audio
  if (tileId === 'cameraCloud') {
    const hasAudio = unlockedFeatures.includes('audioLive');
    if (!hasAudio) {
      return {
        met: false,
        reason: 'Nécessite d’avoir débloqué la Capture Audio (🎙️)',
        requiredTitle: '🎙️ Capture Audio (60 XP)',
      };
    }
  }

  return { met: true };
};

export const UNLOCK_COSTS = {
  // --- Modes & Méthodes de Déclenchement (Triggers - Coût XP) ---
  'shortcutHardware': {
    id: 'shortcutHardware',
    key: 'shortcutHardware',
    label: 'Touche Raccourcis (Volume/Power)',
    type: 'trigger',
    costXP: 60,
    costPV: 0,
    icon: '🔘',
    desc: 'Combinaison discrète des boutons Volume (+/-) ou appuis répétés sur Power (Coût : 60 XP).',
  },
  'smartwatch': {
    id: 'smartwatch',
    key: 'smartwatch',
    label: 'Montre Connectée (Smartwatch)',
    type: 'trigger',
    costXP: 60,
    costPV: 0,
    icon: '⌚',
    desc: 'Bouton SOS discret au poignet (Prérequis : Touche Raccourcis) (Coût : 60 XP).',
  },
  'audioLive': {
    id: 'audioLive',
    key: 'audioLive',
    label: 'Capture Audio',
    type: 'tile',
    costXP: 60,
    costPV: 0,
    icon: '🎙️',
    desc: 'Enregistrement audio d\'urgence 60s scellé dans le Cloud (Coût : 60 XP).',
  },
  'cameraCloud': {
    id: 'cameraCloud',
    key: 'cameraCloud',
    label: 'Capture Vidéo',
    type: 'tile',
    costXP: 60,
    costPV: 0,
    icon: '📷',
    desc: 'Preuve vidéo 4K pour identification faciale (Prérequis : Capture Audio) (Coût : 60 XP).',
  },
  'guardians': {
    id: 'guardians',
    key: 'guardians',
    label: 'Alerter la Communauté (Protecteurs)',
    type: 'tile',
    costXP: 60,
    costPV: 0,
    icon: '👥',
    desc: 'Mobilise les citoyens Protecteurs proches dans le secteur (Coût : 60 XP).',
  },
};

// Aliases de compatibilité
export const XP_UNLOCK_COSTS = UNLOCK_COSTS;

export const DEFAULT_INITIAL_XP = 40; // Minimum initial de 40 XP
export const DEFAULT_INITIAL_PV = 0; // Solde initial de 0 PV libres
export const DEFAULT_INITIAL_AGILITY = 30;
export const DEFAULT_INITIAL_CHARISMA = 25;

export const MAX_AGILITY_CAP = 50;
export const MAX_CHARISMA_CAP = 50;

export const DEFAULT_UNLOCKED_FEATURES = [];

// Plafonds de la ville simulée (cf. handleBuyPopulation / handleBuyProtectors)
export const MAX_CITIZEN_COUNT = 200;
export const MAX_APP_ADOPTION_RATE = 90;

// Liste de toutes les fonctionnalités débloquables (déclenchement + enquête)
export const ALL_UNLOCKABLE_FEATURES = Object.keys(UNLOCK_COSTS);

// Popup unique de félicitations pour la "maîtrise complète" du simulateur
export const loadMasteryPopupShown = () => {
  try {
    return localStorage.getItem('monalerte_mastery_popup_shown') === 'true';
  } catch (e) {
    console.warn("Erreur lecture mastery_popup_shown localStorage", e);
    return false;
  }
};

export const saveMasteryPopupShown = (shown) => {
  try {
    localStorage.setItem('monalerte_mastery_popup_shown', String(Boolean(shown)));
  } catch (e) {
    console.warn("Erreur sauvegarde mastery_popup_shown localStorage", e);
  }
};

// Popup unique de félicitations pour la toute première réussite complète
// (agression interrompue + agresseur identifié), affichée juste avant le premier rapport concerné
export const loadFirstSuccessPopupShown = () => {
  try {
    return localStorage.getItem('monalerte_first_success_popup_shown') === 'true';
  } catch (e) {
    console.warn("Erreur lecture first_success_popup_shown localStorage", e);
    return false;
  }
};

export const saveFirstSuccessPopupShown = (shown) => {
  try {
    localStorage.setItem('monalerte_first_success_popup_shown', String(Boolean(shown)));
  } catch (e) {
    console.warn("Erreur sauvegarde first_success_popup_shown localStorage", e);
  }
};

export const loadUserXP = () => {
  try {
    const stored = localStorage.getItem('monalerte_user_xp');
    if (stored !== null) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed) && parsed >= 0) return parsed;
    }
  } catch (e) {
    console.warn("Erreur lecture XP localStorage", e);
  }
  return DEFAULT_INITIAL_XP;
};

export const saveUserXP = (xp) => {
  try {
    localStorage.setItem('monalerte_user_xp', String(Math.max(0, xp)));
  } catch (e) {
    console.warn("Erreur sauvegarde XP localStorage", e);
  }
};

export const loadUserPV = () => {
  try {
    const stored = localStorage.getItem('monalerte_user_pv');
    if (stored !== null) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed) && parsed >= 0) return parsed;
    }
  } catch (e) {
    console.warn("Erreur lecture PV localStorage", e);
  }
  return DEFAULT_INITIAL_PV;
};

export const saveUserPV = (pv) => {
  try {
    localStorage.setItem('monalerte_user_pv', String(Math.max(0, pv)));
  } catch (e) {
    console.warn("Erreur sauvegarde PV localStorage", e);
  }
};

export const CONTACT_XP_COST = 10;
export const MAX_TRUSTED_CONTACTS = 25;

export const loadTrustedContactsCount = () => {
  try {
    const stored = localStorage.getItem('monalerte_trusted_contacts');
    if (stored !== null) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed) && parsed >= 0) return Math.min(MAX_TRUSTED_CONTACTS, parsed);
    }
  } catch (e) {
    console.warn("Erreur lecture contacts localStorage", e);
  }
  return 0;
};

export const saveTrustedContactsCount = (count) => {
  try {
    localStorage.setItem('monalerte_trusted_contacts', String(Math.min(MAX_TRUSTED_CONTACTS, Math.max(0, count))));
  } catch (e) {
    console.warn("Erreur sauvegarde contacts localStorage", e);
  }
};

export const loadUnlockedFeatures = () => {
  try {
    const stored = localStorage.getItem('monalerte_unlocked_features');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (e) {
    console.warn("Erreur lecture déblocages localStorage", e);
  }
  return DEFAULT_UNLOCKED_FEATURES;
};

export const saveUnlockedFeatures = (featuresList) => {
  try {
    localStorage.setItem('monalerte_unlocked_features', JSON.stringify(featuresList));
  } catch (e) {
    console.warn("Erreur sauvegarde features localStorage", e);
  }
};

export const loadCitizenCount = () => {
  try {
    const stored = localStorage.getItem('monalerte_citizen_count');
    if (stored !== null) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed) && parsed >= 10 && parsed <= 250) return parsed;
    }
  } catch (e) {
    console.warn("Erreur lecture citizen_count localStorage", e);
  }
  return 65;
};

export const saveCitizenCount = (count) => {
  try {
    localStorage.setItem('monalerte_citizen_count', String(count));
  } catch (e) {
    console.warn("Erreur sauvegarde citizen_count localStorage", e);
  }
};

export const loadAppAdoptionRate = () => {
  try {
    const stored = localStorage.getItem('monalerte_app_adoption_rate');
    if (stored !== null) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed) && parsed >= 0 && parsed <= 100) return parsed;
    }
  } catch (e) {
    console.warn("Erreur lecture app_adoption_rate localStorage", e);
  }
  return 0;
};

export const saveAppAdoptionRate = (rate) => {
  try {
    localStorage.setItem('monalerte_app_adoption_rate', String(rate));
  } catch (e) {
    console.warn("Erreur sauvegarde app_adoption_rate localStorage", e);
  }
};

export const loadTimeMultiplier = () => {
  try {
    const stored = localStorage.getItem('monalerte_time_multiplier');
    if (stored !== null) {
      const parsed = parseFloat(stored);
      if (!isNaN(parsed) && parsed > 0 && parsed <= 10) return parsed;
    }
  } catch (e) {
    console.warn("Erreur lecture time_multiplier localStorage", e);
  }
  return 1;
};

export const saveTimeMultiplier = (speed) => {
  try {
    localStorage.setItem('monalerte_time_multiplier', String(speed));
  } catch (e) {
    console.warn("Erreur sauvegarde time_multiplier localStorage", e);
  }
};

// Calculateur des gains d'expérience (XP) et de Points de Vie (PV)
// - STRICTEMENT 0 XP et 0 PV si aucune alerte n'a été lancée
// - +10 XP dès qu'une alerte est réussie et lancée
// - +10 XP si le suspect est arrêté / identifié
// - +10 XP si l'agression n'a pas été au bout (< 100%)
// - +30 XP si les deux conditions sont réunies (combo total)
// - +1 PV pour alerte / action réussie
export const calculateRewards = ({ 
  fleeProgressPct = 100, 
  isIdentified = false, 
  alertTriggered = false, 
  arrivedCount = 0,
  contactsCount = 0,
}) => {
  let totalXP = 0;
  let totalPV = 0;
  const xpBreakdown = [];
  const pvBreakdown = [];

  // SI AUCUNE ALERTE N'A ÉTÉ DÉCLENCHÉE : STRICTEMENT 0 XP & 0 PV
  if (!alertTriggered) {
    return { 
      totalXP: 0, 
      totalPV: 0, 
      xpBreakdown: [{ label: 'Aucune alerte lancée (0 XP)', amount: 0 }], 
      pvBreakdown: [{ label: 'Aucune alerte lancée (0 PV)', amount: 0 }],
      breakdown: [{ label: 'Aucune alerte lancée (0 XP)', amount: 0 }],
    };
  }

  // --- 1. GAINS DE PV (+1 PV pour l'alerte, +1 PV par SMS envoyé, +1 PV par Protecteur au contact) ---
  totalPV += 1;
  pvBreakdown.push({ label: 'Alerte lancée avec succès (+1 PV)', amount: 1, highlight: true });

  if (contactsCount > 0) {
    totalPV += contactsCount;
    pvBreakdown.push({
      label: `📲 ${contactsCount} SMS d'urgence envoyé(s) aux contacts (+${contactsCount} PV)`,
      amount: contactsCount,
      highlight: true
    });
  }

  if (arrivedCount > 0) {
    totalPV += arrivedCount;
    pvBreakdown.push({
      label: `🛡️ ${arrivedCount} Protecteur(s) au contact sur les lieux (+${arrivedCount} PV)`,
      amount: arrivedCount,
      highlight: true
    });
  }

  // --- 2. GAINS D'XP (+10 XP dès qu'une alerte est lancée et réussie, +1 XP par SMS envoyé, +1 XP par Protecteur au contact) ---
  totalXP += 10;
  xpBreakdown.push({
    label: '⚡ Alerte MonAlerte lancée et réussie (+10 XP)',
    amount: 10,
    highlight: true
  });

  if (contactsCount > 0) {
    totalXP += contactsCount;
    xpBreakdown.push({
      label: `📲 ${contactsCount} SMS d'urgence envoyé(s) aux contacts (+${contactsCount} XP)`,
      amount: contactsCount,
      highlight: true
    });
  }

  if (arrivedCount > 0) {
    totalXP += arrivedCount;
    xpBreakdown.push({
      label: `🛡️ ${arrivedCount} Protecteur(s) au contact sur les lieux (+${arrivedCount} XP)`,
      amount: arrivedCount,
      highlight: true
    });
  }

  const isAttackInterrupted = Boolean(fleeProgressPct < 100);
  const isSuspectArrested = Boolean(isIdentified);

  if (isAttackInterrupted && isSuspectArrested) {
    totalXP += 30;
    xpBreakdown.push({ 
      label: '🏆 Succès Total : Suspect arrêté & Agression interrompue (+30 XP)', 
      amount: 30, 
      highlight: true 
    });
  } else if (isSuspectArrested) {
    totalXP += 10;
    xpBreakdown.push({ 
      label: '🎯 Suspect arrêté / identifié (+10 XP)', 
      amount: 10, 
      highlight: true 
    });
  } else if (isAttackInterrupted) {
    totalXP += 10;
    xpBreakdown.push({ 
      label: `🛡️ Agression interrompue avant son terme (${fleeProgressPct}%) (+10 XP)`, 
      amount: 10, 
      highlight: true 
    });
  }

  return { 
    totalXP, 
    totalPV, 
    xpBreakdown, 
    pvBreakdown,
    breakdown: xpBreakdown,
  };
};

export const calculateXPRewards = calculateRewards;
